/*
 * @Description: Old Way vs FalcoCut comparison section for AI Video Editor
 */
import React from 'react';

import StartButton from './StartButton';

const oldWayItems: string[] = [
  '2\u20116 hours lost per video instead of creating',
  'Multiple apps needed for captions, cuts, and effects',
  'Manual edits for every clip, every platform, every format',
  '$100\u2013$500 per project if outsourcing',
  'Constant back\u2011and\u2011forth steps that slow down the entire workflow',
];

const newWayItems: string[] = [
  'AI edits done in minutes',
  'One tool replaces 5+ creator apps',
  'Edit with simple text prompts \u2014 no editing skills needed',
  'Lower editing costs by automating repetitive tasks',
  'A streamlined workflow that keeps every step in one place',
];

const ComparisonSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Old Way vs. FalcoCut AI Video Editor
        </h2>

        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Editing used to mean long nights, complicated software, and endless tiny fixes.
          FalcoCut lets you edit videos in seconds with simple prompts &mdash; so you can
          focus on creating, posting, and growing your audience instead of fighting
          timelines and tools.
        </p>

        <div className="grid grid-cols-1 pad:grid-cols-2 gap-6 pad:gap-8 mb-10 pad:mb-14">
          {/* Old Way Card */}
          <div className="rounded-2xl border border-gray-200 p-6 pad:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">Old Way</h3>
            </div>

            <ul className="space-y-4 mb-6">
              {oldWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-gray-400 mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-base pad:text-base font-semibold text-gray-800">
              <span>{'\uD83D\uDE2B'}</span>
              <span>Slow, expensive, and hard to scale.</span>
            </div>
          </div>

          {/* New Way Card */}
          <div className="rounded-2xl border-2 border-[#7C3AED] bg-[#7C3AED]/5 p-6 pad:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">
                New Way with FalcoCut
              </h3>
            </div>

            <ul className="space-y-4 mb-6">
              {newWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-[#7C3AED] mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-base pad:text-base font-semibold text-[#7C3AED]">
              <span>{'\uD83D\uDE80'}</span>
              <span>Fast, affordable, and built for scale.</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <StartButton text="Try Video Editing" />
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
